import { Show } from '@refinedev/antd';
import {
  Card,
  Row,
  Col,
  Statistic,
  Tag,
  Button,
  Space,
  Descriptions,
  Table,
  Spin,
  Empty,
  message,
} from 'antd';
import { CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { Line } from '@ant-design/charts';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';
import { PUBLIC_LINK_BASE, CHANNEL_COLORS, CHANNEL_OPTIONS } from './constants';

interface TrackingLink {
  id: number;
  slug: string;
  name: string;
  channel: string;
  targetUrl: string;
  description: string | null;
  isActive: boolean;
  clickCount: number;
  uniqueClickCount: number;
  createdAt: string;
  createdByStaff: { fullName: string | null; username: string } | null;
}

interface StatsData {
  link: TrackingLink;
  summary: {
    total: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
    uniqueTotal: number;
    uniqueToday: number;
    uniqueThisWeek: number;
    uniqueThisMonth: number;
  };
  byDay: Array<{ date: string; clicks: number; uniqueClicks: number }>;
  topReferers: Array<{ referer: string | null; clicks: number }>;
}

export const TrackingLinkShow = () => {
  const { id } = useParams();
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await apiClient.get<{ data: StatsData }>(
        `/admin/tracking-links/${id}/stats?days=30`,
      );
      setData(res.data.data ?? (res.data as any));
    } catch {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const link = data?.link;
  const publicUrl = link ? `${PUBLIC_LINK_BASE}/${link.slug}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      message.success(t('common.copied') || 'Copied');
    } catch {
      message.error(t('common.error'));
    }
  };

  return (
    <Show title={link?.name ?? 'Tracking link'} isLoading={loading}>
      {loading && !data ? (
        <Spin />
      ) : !data ? (
        <Empty />
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Jami bosilgan" value={data.summary.total} />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Unikal: <b>{data.summary.uniqueTotal}</b>
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="Bugun" value={data.summary.today} />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Unikal: <b>{data.summary.uniqueToday}</b>
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="7 kunda" value={data.summary.thisWeek} />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Unikal: <b>{data.summary.uniqueThisWeek}</b>
                </div>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card>
                <Statistic title="30 kunda" value={data.summary.thisMonth} />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Unikal: <b>{data.summary.uniqueThisMonth}</b>
                </div>
              </Card>
            </Col>
          </Row>

          <Card style={{ marginBottom: 16 }} title="Havola maʼlumoti">
            <Descriptions column={{ xs: 1, md: 2 }}>
              <Descriptions.Item label="Nomi">{link!.name}</Descriptions.Item>
              <Descriptions.Item label="Kanal">
                <Tag color={CHANNEL_COLORS[link!.channel] || 'default'}>
                  {CHANNEL_OPTIONS.find((o) => o.value === link!.channel)
                    ?.label || link!.channel}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Slug">
                <code>{link!.slug}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Holat">
                <Tag color={link!.isActive ? 'green' : 'red'}>
                  {link!.isActive ? 'Faol' : 'Nofaol'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Yaratilgan">
                {new Date(link!.createdAt).toLocaleString('uz-UZ')}
              </Descriptions.Item>
              {link!.createdByStaff && (
                <Descriptions.Item label="Yaratgan">
                  {link!.createdByStaff.fullName || link!.createdByStaff.username}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Qisqa havola" span={2}>
                <Space wrap>
                  <a href={publicUrl} target="_blank" rel="noreferrer">
                    <LinkOutlined /> {publicUrl}
                  </a>
                  <Button icon={<CopyOutlined />} size="small" onClick={copyLink}>
                    Nusxa olish
                  </Button>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Maqsadli URL" span={2}>
                <a href={link!.targetUrl} target="_blank" rel="noreferrer">
                  {link!.targetUrl}
                </a>
              </Descriptions.Item>
              {link!.description && (
                <Descriptions.Item label="Izoh" span={2}>
                  {link!.description}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <Card title="Kunlik bosilishlar (30 kun)" style={{ marginBottom: 16 }}>
            {data.byDay.length > 0 ? (
              <Line
                // Pivot to long-form so the chart can draw two series.
                // Both metrics share the same y-axis (click-equivalents).
                data={data.byDay.flatMap((d) => [
                  { date: d.date, value: d.clicks, series: 'Jami' },
                  { date: d.date, value: d.uniqueClicks, series: 'Unikal' },
                ])}
                xField="date"
                yField="value"
                seriesField="series"
                height={260}
                smooth
                point={{ size: 3 }}
                color={['#1890ff', '#13c2c2']}
                xAxis={{ tickCount: 8 }}
                legend={{ position: 'top' }}
              />
            ) : (
              <Empty description="Maʼlumot yo'q" />
            )}
          </Card>

          <Card title="Top yo'naltiruvchilar">
            {data.topReferers.length > 0 ? (
              <Table
                dataSource={data.topReferers}
                rowKey={(r) => r.referer ?? 'unknown'}
                pagination={false}
                size="small"
                columns={[
                  { dataIndex: 'referer', title: 'Referer' },
                  { dataIndex: 'clicks', title: 'Bosilgan', width: 100 },
                ]}
              />
            ) : (
              <Empty description="Hali maʼlumot yo'q" />
            )}
          </Card>
        </>
      )}
    </Show>
  );
};
